import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { Modal } from 'antd';
import * as XLSX from 'xlsx';
import type { HierarchyView, NodeKind } from './DefineBoundaryStep';
import {
  useLazyGetHierarchyTemplateQuery,
  useUploadHierarchyMutation,
  useGetNodeTreeQuery,
  useCreateNodeAreaMutation,
  useDeleteNodeAreaMutation,
  useUpdateNodeAreaMutation,
  type OrgNode,
} from '../../../features/organizations/organizationsApi';
import { territoryApi, type BoothInfoDto } from '../../../features/territory/territoryApi';
import { useAppDispatch } from '../../../app/hooks';
import type { AppDispatch } from '../../../app/store';
import { Spinner } from '../../../components/ui';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  ChevronRightIcon,
  FilterIcon,
  ColumnsIcon,
  KebabIcon,
  ArrowRightIcon,
  LinkIcon,
  TrashIcon,
  TemplateDocIcon,
  BulkUploadIcon,
  HierarchyNodesIcon,
  NodeBoothIcon,
} from '../../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/** The org's internal hierarchy tree from /organisation/node/tree/ (left panel). */
function InternalOrgTree({
  nodes,
  onBoothClick,
  activeCode,
}: {
  nodes: OrgNode[];
  /** Clicking a booth area asks the EC panel to locate it (by area_code = ps_code). */
  onBoothClick?: (areaCode: string, boothName: string) => void;
  /** area_code currently being located (highlighted). */
  activeCode?: string | null;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: OrgNode, id: string) => {
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = !collapsed.has(id);
    return (
      <div key={id}>
        <button
          type="button"
          onClick={() => hasChildren && toggle(id)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold tracking-[0.24px] text-ink transition-colors hover:bg-brand-shade/60"
        >
          {hasChildren ? (
            <CaretIcon
              className={['size-3 shrink-0 text-ink-secondary', open ? '' : '-rotate-90'].join(' ')}
            />
          ) : (
            <span className="size-3 shrink-0" />
          )}
          <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
            {node.level_name}
          </span>
          <span className="truncate">{node.org_name}</span>
          {areas.length > 0 && (
            <span className="ml-auto shrink-0 rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]">
              {areas.length} booth{areas.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
        {open && hasChildren && (
          <div className="ml-[13px] flex flex-col gap-1 border-l border-[rgba(195,198,215,0.5)] pl-3">
            {kids.map((c, i) => renderNode(c, `${id}.${i}`))}
            {areas.map((a, i) => {
              const active = activeCode != null && a.area_code === activeCode;
              return (
                <button
                  key={`${id}-a-${i}`}
                  type="button"
                  onClick={() => onBoothClick?.(a.area_code, a.booth_name || a.area_code)}
                  className={[
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                    active ? 'bg-brand-shade' : 'hover:bg-brand-shade/60',
                  ].join(' ')}
                >
                  <NodeBoothIcon className="size-4 shrink-0 text-brand" />
                  <span className="truncate font-medium text-ink">
                    {a.booth_name || a.area_code}
                  </span>
                  <span className="ml-auto shrink-0 text-[10px] text-ink-secondary">
                    {a.area_code}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1">{nodes.map((n, i) => renderNode(n, `n${i}`))}</div>;
}

/* ------------------------------------------------------------------ *
 * Destination miller-columns (Election Commission Structure)
 * ------------------------------------------------------------------ */

interface DestNode {
  id: string;
  label: string;
  /** Kind + code + context — used to lazily fetch this node's children on drill. */
  kind?: NodeKind;
  code?: string;
  acCode?: string;
  branch?: 'urban' | 'rural';
  /** Pre-loaded children (from the step-2 selection path); deeper levels load lazily. */
  children?: DestNode[];
  /** Was this node checked in step 2 — the level to auto-open in step 4. */
  selected?: boolean;
  /** Booth nodes can be mapped to a source unit. */
  linkable?: boolean;
  /** Booth area/ps code — matched against the Internal Organization tree to auto-detect links. */
  areaCode?: string;
}

/** A booth already present in the Internal Organization tree, keyed by area_code. */
interface LinkedArea {
  /** node-area record id — the target of the de-link (DELETE) / re-link (PATCH) calls. */
  nodeAreaId?: number;
  boothName: string;
  orgName: string;
}

/**
 * Index every booth area_code in the Internal Organization tree (from the upload)
 * so an EC booth sharing that code can be shown as already linked (and de-/re-linked).
 */
function collectInternalAreas(nodes: OrgNode[]): Map<string, LinkedArea> {
  const map = new Map<string, LinkedArea>();
  const walk = (n: OrgNode) => {
    (n.areas ?? []).forEach((a) => {
      if (a.area_code) {
        map.set(a.area_code, {
          nodeAreaId: a.id,
          boothName: a.booth_name || a.area_code,
          orgName: n.org_name,
        });
      }
    });
    (n.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return map;
}

const run = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();

/** "State › PC › AC › … › Booth" path from a booth-info record. */
function boothLocationPath(info: BoothInfoDto): string {
  return [
    info.state?.name,
    info.parliament?.name,
    info.assembly?.name,
    info.gram_panchayat?.name,
    info.village?.name,
    info.booth?.name,
  ]
    .filter(Boolean)
    .join(' › ');
}

/**
 * Build the EC structure (top-down DestNode tree) for one booth from its booth-info:
 * State → Parliament → Assembly → [Gram Panchayat → Village] → Booth. Levels that are
 * null in the response are skipped.
 */
function boothInfoToTree(info: BoothInfoDto): DestNode[] {
  const psCode = info.booth?.ps_code || '';
  const base = `loc-${psCode || 'booth'}`;
  const boothLabel = `${info.booth?.name || 'Booth'}${psCode ? ` · ${psCode}` : ''}`;

  let child: DestNode = {
    id: `${base}/booth`,
    label: boothLabel,
    linkable: true,
    areaCode: psCode || undefined,
  };
  if (info.village?.name) {
    child = { id: `${base}/village`, label: info.village.name, children: [child] };
  }
  if (info.gram_panchayat?.name) {
    child = { id: `${base}/gp`, label: info.gram_panchayat.name, children: [child] };
  }
  const acLabel = `${info.assembly?.name || 'Assembly'}${
    info.assembly?.ac_code ? ` · ${info.assembly.ac_code}` : ''
  }`;
  const acNode: DestNode = { id: `${base}/ac`, label: acLabel, children: [child] };
  const pcNode: DestNode = {
    id: `${base}/pc`,
    label: info.parliament?.name || 'Parliament',
    children: [acNode],
  };
  const stateNode: DestNode = {
    id: `${base}/state`,
    label: info.state?.name || 'State',
    children: [pcNode],
  };
  return [stateNode];
}

/**
 * Convert the step-2 selection path into base DestNodes, stopping at the chosen
 * (selected) node. Ancestors keep their known children (State → PC → AC); below the
 * selected node, children are loaded lazily on drill. `acCode` is carried down so
 * rural/urban branches can query the right assembly.
 */
function toBaseTree(views: HierarchyView[], acCode?: string): DestNode[] {
  return views.map((v) => {
    const nextAc = v.kind === 'assembly' ? v.code : acCode;
    const branch =
      v.kind === 'branch' ? (v.label.toLowerCase().includes('urban') ? 'urban' : 'rural') : undefined;
    const node: DestNode = {
      id: v.id,
      label: v.label,
      kind: v.kind,
      code: v.code,
      acCode: nextAc,
      branch,
      areaCode: v.areaCode,
      selected: v.selected,
      linkable: v.kind === 'booth',
    };
    if (!v.selected && (v.children?.length ?? 0) > 0) {
      node.children = toBaseTree(v.children, nextAc);
    }
    return node;
  });
}

/** The node checked in step 2 (deepest selected) — its children are auto-loaded in step 4. */
function findSelectedNode(nodes: DestNode[]): DestNode | undefined {
  for (const n of nodes) {
    if (n.selected) return n;
    const found = n.children ? findSelectedNode(n.children) : undefined;
    if (found) return found;
  }
  return undefined;
}

/** Fetch one node's direct children from the territory API — a single call per drill. */
async function fetchDestChildren(dispatch: AppDispatch, node: DestNode): Promise<DestNode[]> {
  const ep = territoryApi.endpoints;
  const cid = (suffix: string) => `${node.id}/${suffix}`;
  switch (node.kind) {
    case 'state': {
      const pcs = await run(dispatch(ep.getParliaments.initiate({ state: node.code! }, { subscribe: false })));
      return pcs.map((p) => ({ id: cid(`pc-${p.id}`), label: p.name || `PC ${p.id}`, kind: 'parliament', code: String(p.id) }));
    }
    case 'parliament': {
      const acs = await run(dispatch(ep.getAssemblies.initiate({ pc: node.code! }, { subscribe: false })));
      return acs.map((a) => ({
        id: cid(`ac-${a.id}`),
        label: a.name || `AC ${a.id}`,
        kind: 'assembly',
        code: a.ac_code || '',
        acCode: a.ac_code || '',
      }));
    }
    case 'assembly':
      return [
        { id: cid('rural'), label: 'Rural', kind: 'branch', branch: 'rural', code: '', acCode: node.code },
        { id: cid('urban'), label: 'Urban', kind: 'branch', branch: 'urban', code: '', acCode: node.code },
      ];
    case 'branch': {
      if (node.branch === 'urban') {
        const ulbs = await run(dispatch(ep.getUlbs.initiate(undefined, { subscribe: false })));
        return ulbs.map((u) => ({
          id: cid(`ulb-${u.block_ulb_code ?? u.block_pk}`),
          label: u.name_en || `ULB ${u.block_pk}`,
          kind: 'ulb',
          code: u.block_ulb_code || '',
        }));
      }
      const gps = await run(dispatch(ep.getGramPanchayats.initiate({ acCode: node.acCode || '' }, { subscribe: false })));
      return gps.map((g) => ({
        id: cid(`gp-${g.gram_panchayat_lgd_code ?? g.gram_panchayat_pk}`),
        label: g.name_en || `GP ${g.gram_panchayat_pk}`,
        kind: 'gram',
        code: g.gram_panchayat_lgd_code || '',
      }));
    }
    case 'ulb': {
      const wards = await run(dispatch(ep.getWards.initiate({ ulbCode: node.code! }, { subscribe: false })));
      return wards.map((w) => ({
        id: cid(`ward-${w.ward_code ?? w.ward_pk}`),
        label: w.ward_name_en || `Ward ${w.ward_pk}`,
        kind: 'ward',
        code: w.ward_code || '',
      }));
    }
    case 'gram': {
      const vs = await run(dispatch(ep.getRuralVillages.initiate({ gpCode: node.code! }, { subscribe: false })));
      return vs.map((v) => ({
        id: cid(`vil-${v.village_result_code ?? v.village_pk}`),
        label: v.name_en || `Village ${v.village_pk}`,
        kind: 'village',
        code: v.village_result_code || '',
      }));
    }
    case 'ward': {
      const vs = await run(dispatch(ep.getUrbanVillages.initiate({ wardCode: node.code! }, { subscribe: false })));
      return vs.map((v) => ({
        id: cid(`vil-${v.village_result_code ?? v.village_pk}`),
        label: v.name_en || `Village ${v.village_pk}`,
        kind: 'village',
        code: v.village_result_code || '',
      }));
    }
    case 'village': {
      const booths = await run(dispatch(ep.getBooths.initiate({ villageResultCode: node.code! }, { subscribe: false })));
      return booths.map((b) => ({
        id: cid(`booth-${b.ps_code}`),
        label: b.booth_name ? `${b.booth_name} · ${b.ps_code}` : `Booth ${b.ps_code}`,
        kind: 'booth',
        linkable: true,
        areaCode: b.ps_code,
      }));
    }
    default:
      return [];
  }
}

/** Every expandable node id in the org tree (has children or booth areas). */
function expandableNodeIds(nodes: OrgNode[]): string[] {
  const ids: string[] = [];
  const walk = (n: OrgNode, id: string) => {
    const kids = n.children ?? [];
    if (kids.length > 0 || (n.areas ?? []).length > 0) ids.push(id);
    kids.forEach((c, i) => walk(c, `${id}.${i}`));
  };
  nodes.forEach((n, i) => walk(n, `n${i}`));
  return ids;
}

/**
 * The real Internal Organization structure in the re-link popup — drill and pick the
 * org node (SK) to move a booth to. Booth areas are shown for context only; the
 * selection returns the chosen node's `organisation_node` id.
 */
function SourceMapTree({
  nodes,
  selectedNodeId,
  onSelect,
}: {
  nodes: OrgNode[];
  selectedNodeId: number | null;
  onSelect: (nodeId: number, label: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(expandableNodeIds(nodes)));
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: OrgNode, id: string, depth: number) => {
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = expanded.has(id);
    const selectable = node.id != null;
    const selected = selectable && selectedNodeId === node.id;
    return (
      <div key={id}>
        <div
          className={[
            'flex items-center gap-2 rounded-md py-1.5 pr-2 text-xs transition-colors',
            selected ? 'bg-[rgba(0,74,198,0.1)]' : 'hover:bg-brand-shade/60',
          ].join(' ')}
          style={{ paddingLeft: depth * 16 + 8 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggle(id)}
              className="flex size-4 shrink-0 items-center justify-center text-ink-secondary"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              <CaretIcon className={['size-3', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <button
            type="button"
            disabled={!selectable}
            onClick={() => selectable && onSelect(node.id!, node.org_name)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-not-allowed"
          >
            <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
              {node.level_name}
            </span>
            <span
              className={['truncate', selected ? 'font-semibold text-brand' : 'text-ink'].join(' ')}
            >
              {node.org_name}
            </span>
          </button>
          {selected && <CheckmarkIcon className="size-4 shrink-0 text-brand" />}
        </div>
        {open && hasChildren && (
          <>
            {kids.map((c, i) => renderNode(c, `${id}.${i}`, depth + 1))}
            {areas.map((a, i) => (
              <div
                key={`${id}.a${i}`}
                className="flex items-center gap-2 rounded-md py-1.5 pr-2 text-xs text-ink-secondary"
                style={{ paddingLeft: (depth + 1) * 16 + 8 }}
              >
                <span className="size-4 shrink-0" />
                <NodeBoothIcon className="size-4 shrink-0 text-ink-secondary/60" />
                <span className="truncate">{a.booth_name || a.area_code}</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-sm text-ink-secondary">
        Upload the hierarchy Excel first to populate the Internal Organization structure.
      </p>
    );
  }

  return (
    <div className="max-h-[360px] overflow-auto">
      {nodes.map((n, i) => renderNode(n, `n${i}`, 0))}
    </div>
  );
}

/** Row-level booth link actions (linked badge + relink/unlink, or a Link button). */
function BoothActions({
  node,
  link,
  busy,
  onUnlink,
  onOpenRelink,
  onOpenLink,
}: {
  node: DestNode;
  link: LinkedArea | undefined;
  busy: boolean;
  onUnlink: (nodeAreaId: number) => void;
  onOpenRelink: (nodeAreaId: number, label: string) => void;
  onOpenLink: (areaCode: string, boothName: string) => void;
}) {
  const nodeAreaId = link?.nodeAreaId;
  if (link) {
    return (
      <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <span
          className="flex max-w-[140px] items-center gap-1 rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]"
          title={`Linked to ${link.orgName} · ${link.boothName}`}
        >
          <LinkIcon className="size-3 shrink-0" />
          <span className="truncate">{link.orgName}</span>
        </span>
        {nodeAreaId != null && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => onOpenRelink(nodeAreaId, node.label)}
              className="rounded px-1 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:opacity-50"
              title="Move this booth to another org node"
            >
              Relink
            </button>
            <button
              type="button"
              aria-label="Unlink"
              disabled={busy}
              onClick={() => onUnlink(nodeAreaId)}
              className="flex size-6 items-center justify-center rounded text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
            >
              <TrashIcon className="size-3.5" />
            </button>
          </>
        )}
      </span>
    );
  }
  if (node.areaCode) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onOpenLink(node.areaCode!, node.label);
        }}
        className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:opacity-50"
      >
        <LinkIcon className="size-3" />
        Link
      </button>
    );
  }
  return null;
}

type GetChildren = (node: DestNode) => DestNode[] | undefined;

/** The id chain of a node and its already-loaded single-child descendants. */
function branchFrom(node: DestNode | undefined, getChildren: GetChildren): string[] {
  const ids: string[] = [];
  let n = node;
  while (n) {
    ids.push(n.id);
    n = getChildren(n)?.[0];
  }
  return ids;
}

/**
 * The path (ids) from a root down to the node checked in step 2 — so step 4 auto-opens
 * exactly the level the user selected (e.g. Urban), not just the first branch.
 */
function firstBranchPath(tree: DestNode[], getChildren: GetChildren): string[] {
  const dfs = (node: DestNode): string[] | null => {
    if (node.selected) return [node.id];
    for (const k of getChildren(node) ?? []) {
      const sub = dfs(k);
      if (sub) return [node.id, ...sub];
    }
    return null;
  };
  for (const root of tree) {
    const p = dfs(root);
    if (p) return p;
  }
  // Fallback (nothing selected): open the first loaded chain.
  return tree[0] ? branchFrom(tree[0], getChildren) : [];
}

/**
 * Miller-columns view of the Election Commission Structure (matches the Figma): a
 * breadcrumb of the drilled path over horizontal LEVEL-N columns (State → … → Booth).
 * Booth rows carry the link / relink / unlink actions.
 */
function DestinationColumns({
  tree,
  getChildren,
  onDrill,
  loadingNodes,
  linkedAreas,
  sourceTree,
  onLink,
  onUnlink,
  onRelink,
  busyAreaId,
  busyCode,
}: {
  tree: DestNode[];
  /** A node's already-loaded children (undefined = not loaded yet). */
  getChildren: GetChildren;
  /** Load a node's children lazily when it's drilled into. */
  onDrill: (node: DestNode) => void;
  /** Node ids currently fetching their children (shows a loading column). */
  loadingNodes: Set<string>;
  /** Booths already present in the Internal Organization tree, keyed by area_code. */
  linkedAreas: Map<string, LinkedArea>;
  /** The Internal Organization tree — the org nodes a booth can be (re)linked to. */
  sourceTree: OrgNode[];
  /** Link an unlinked booth to an org node (POST a new node-area record). */
  onLink: (areaCode: string, boothName: string, organisationNodeId: number) => void;
  /** De-link a booth (DELETE its node-area record). */
  onUnlink: (nodeAreaId: number) => void;
  /** Re-link a booth: move its node-area to another org node (PATCH). */
  onRelink: (nodeAreaId: number, organisationNodeId: number) => void;
  /** node-area id currently being de-/re-linked (disables its actions). */
  busyAreaId: number | null;
  /** area_code of an unlinked booth currently being linked (disables its action). */
  busyCode: string | null;
}) {
  // Open the path down to the selected level (loaded from step 2) on mount.
  const [path, setPath] = useState<string[]>(() => firstBranchPath(tree, getChildren));
  const [linkTarget, setLinkTarget] = useState<
    | { mode: 'relink'; nodeAreaId: number; label: string }
    | { mode: 'link'; areaCode: string; boothName: string; label: string }
    | null
  >(null);
  const [mapSel, setMapSel] = useState<{ id: number; label: string } | null>(null);

  const openRelink = (nodeAreaId: number, label: string) => {
    setLinkTarget({ mode: 'relink', nodeAreaId, label });
    setMapSel(null);
  };
  const openLink = (areaCode: string, boothName: string) => {
    setLinkTarget({ mode: 'link', areaCode, boothName, label: boothName });
    setMapSel(null);
  };
  const closeLink = () => {
    setLinkTarget(null);
    setMapSel(null);
  };

  // Re-open the selected path when the selection (tree) changes.
  useEffect(() => {
    setPath(firstBranchPath(tree, getChildren));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  // Clicking a row selects it in its column and lazily loads its children.
  const select = (colIndex: number, node: DestNode) => {
    setPath((prev) => [...prev.slice(0, colIndex), node.id]);
    onDrill(node);
  };

  // Build the level columns + breadcrumb from the drilled path (lazy children).
  const columns: DestNode[][] = [tree];
  const crumbs: string[] = [];
  let cursor: DestNode[] = tree;
  let loadingCol = false;
  for (let i = 0; i < path.length; i += 1) {
    const node = cursor.find((n) => n.id === path[i]);
    if (!node) break;
    crumbs.push(node.label);
    const kids = getChildren(node);
    if (kids && kids.length > 0) {
      columns.push(kids);
      cursor = kids;
    } else {
      if (node.kind !== 'booth' && loadingNodes.has(node.id)) loadingCol = true;
      break;
    }
  }

  return (
    <>
      {/* Breadcrumb of the drilled path */}
      <div className="flex shrink-0 items-center gap-2 overflow-auto border-b border-[#c3c6d7] bg-[#eff4ff] px-4 pb-[13px] pt-3">
        {crumbs.map((label, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={`${label}-${i}`} className="flex shrink-0 items-center gap-2">
              <span
                className={[
                  'text-[11px] font-medium tracking-[0.55px]',
                  last ? 'text-ink' : 'text-brand',
                ].join(' ')}
              >
                {label}
              </span>
              {!last && (
                <ChevronRightIcon className="h-[7px] w-[4.3px] shrink-0 text-ink-secondary" />
              )}
            </span>
          );
        })}
      </div>

      {/* Level columns — reversed view: deepest level (Booth) first, State last. */}
      <div className="flex-1 overflow-x-auto bg-[#f8f9ff]">
        <div className="flex h-full min-w-full">
          {loadingCol && (
            <div className="flex w-[280px] shrink-0 items-center justify-center border-r border-[#c3c6d7]">
              <Spinner />
            </div>
          )}
          {columns
            .map((items, colIndex) => ({ items, colIndex }))
            .reverse()
            .map(({ items, colIndex }) => (
              <div
                key={colIndex}
                className="flex w-[280px] shrink-0 flex-col border-r border-[#c3c6d7] last:border-r-0"
              >
              <div className="shrink-0 border-b border-[#c3c6d7] bg-[rgba(229,238,255,0.3)] px-4 pb-[13px] pt-3 text-[12px] font-semibold uppercase tracking-[0.6px] text-ink">
                Level {colIndex + 1}
              </div>
              <div className="flex flex-1 flex-col gap-1 overflow-auto p-2">
                {items.map((node) => {
                  const canDrill = node.kind !== 'booth';
                  const isBooth = Boolean(node.linkable);
                  const active = path[colIndex] === node.id;
                  const link = node.areaCode ? linkedAreas.get(node.areaCode) : undefined;
                  const nodeAreaId = link?.nodeAreaId;
                  const busy =
                    (nodeAreaId != null && busyAreaId === nodeAreaId) ||
                    (node.areaCode != null && busyCode === node.areaCode);
                  return (
                    <div
                      key={node.id}
                      onClick={() => select(colIndex, node)}
                      className={[
                        'flex cursor-pointer items-center gap-2 rounded-lg p-[9px] text-left transition-colors',
                        active
                          ? 'border border-[rgba(0,74,198,0.2)] bg-[rgba(0,74,198,0.1)]'
                          : 'border border-transparent hover:bg-brand-shade/50',
                      ].join(' ')}
                    >
                      {/* Inverted view: drill arrow first (points left toward the deeper levels). */}
                      {!isBooth && canDrill && (
                        <ChevronRightIcon className="h-[10px] w-[6px] shrink-0 rotate-180 text-ink-secondary" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-right text-[13px]">
                        <span
                          className={
                            active ? 'font-semibold text-[#00174b]' : 'font-normal text-ink'
                          }
                        >
                          {node.label}
                        </span>
                      </span>
                      {isBooth && (
                        <span className="flex shrink-0 items-center gap-1.5">
                          <BoothActions
                            node={node}
                            link={link}
                            busy={busy}
                            onUnlink={onUnlink}
                            onOpenRelink={openRelink}
                            onOpenLink={openLink}
                          />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Link / re-link popup — connect a booth to an org node */}
      <Modal
        open={Boolean(linkTarget)}
        onCancel={closeLink}
        footer={null}
        width={560}
        title={
          linkTarget
            ? `${linkTarget.mode === 'link' ? 'Link' : 'Relink'} “${linkTarget.label}”`
            : ''
        }
      >
        <p className="mb-3 text-sm text-ink-secondary">
          Select the organization node to {linkTarget?.mode === 'link' ? 'link' : 'move'} this booth
          to.
        </p>
        <div className="rounded-lg border border-line p-2">
          <SourceMapTree
            nodes={sourceTree}
            selectedNodeId={mapSel?.id ?? null}
            onSelect={(id, label) => setMapSel({ id, label })}
          />
        </div>
        {mapSel && (
          <p className="mt-3 text-sm text-ink-secondary">
            {linkTarget?.mode === 'link' ? 'Linking' : 'Moving'} to{' '}
            <span className="font-semibold text-ink">{mapSel.label}</span>
          </p>
        )}
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeLink}
            className="rounded-lg border border-line px-5 py-2 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!mapSel || !linkTarget}
            onClick={() => {
              if (linkTarget && mapSel) {
                if (linkTarget.mode === 'relink') onRelink(linkTarget.nodeAreaId, mapSel.id);
                else onLink(linkTarget.areaCode, linkTarget.boothName, mapSel.id);
              }
              closeLink();
            }}
            className="rounded-lg bg-brand px-6 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-40"
          >
            {linkTarget?.mode === 'link' ? 'Link' : 'Relink'}
          </button>
        </div>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Info cards
 * ------------------------------------------------------------------ */

function InfoCard({
  Icon,
  iconClass,
  badgeClass,
  title,
  subtitle,
  action,
}: {
  Icon: IconType;
  iconClass: string;
  badgeClass: string;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 items-center justify-between gap-4 rounded-xl border border-line bg-white p-6 shadow-card">
      <div className="flex items-center gap-4">
        <span
          className={['flex size-12 shrink-0 items-center justify-center rounded-full', badgeClass, iconClass].join(' ')}
        >
          <Icon className="h-5 w-4" />
        </span>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-ink">{title}</span>
          <span className="text-[13px] text-ink-secondary">{subtitle}</span>
        </div>
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

const PILL = 'rounded bg-[#dce9ff] px-2 py-1 text-[11px] font-medium uppercase tracking-[0.55px] text-ink';

const FALLBACK_LEVELS = ['Alwar BJP', 'Mandal', 'Shakti Kendra', 'Booths'];

/** Step 4 — Organization Hierarchy Data Import. */
export default function DataImportStep({
  active = true,
  onConfirm,
  hierarchyLevels,
  selection,
  organizationId,
}: {
  /** This step is the one currently shown — gates its data fetches. */
  active?: boolean;
  onConfirm: () => void;
  hierarchyLevels?: string[];
  /** The real selected hierarchy from step 2 (state → … → booth). */
  selection?: HierarchyView[];
  organizationId?: number | null;
}) {
  const dispatch = useAppDispatch();

  // Base tree = the step-2 path down to the chosen level (State → PC → AC). Deeper
  // levels (Rural/Urban → GP/Ward → Village → Booth) load lazily on drill, so we don't
  // fetch every village's booths up front.
  const destTree = useMemo(() => {
    const roots = (selection ?? []).flatMap((r) => (r.children.length > 0 ? r.children : [r]));
    return toBaseTree(roots);
  }, [selection]);

  // Lazily-loaded children keyed by node id, and the nodes currently fetching.
  const [childrenByNode, setChildrenByNode] = useState<Record<string, DestNode[]>>({});
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  // Reset the lazy cache when the selection changes.
  useEffect(() => {
    setChildrenByNode({});
    setLoadingNodes(new Set());
  }, [destTree]);

  const getChildren = useCallback(
    (node: DestNode): DestNode[] | undefined => childrenByNode[node.id] ?? node.children,
    [childrenByNode],
  );
  const loadChildren = useCallback(
    async (node: DestNode) => {
      if (
        node.kind === 'booth' ||
        childrenByNode[node.id] ||
        (node.children?.length ?? 0) > 0 ||
        loadingNodes.has(node.id)
      ) {
        return;
      }
      setLoadingNodes((s) => new Set(s).add(node.id));
      try {
        const kids = await fetchDestChildren(dispatch, node);
        setChildrenByNode((prev) => ({ ...prev, [node.id]: kids }));
      } catch {
        /* ignore — that level just won't expand */
      } finally {
        setLoadingNodes((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [dispatch, childrenByNode, loadingNodes],
  );

  // Auto-load the children of the level chosen in step 2 (e.g. Rural → its Gram
  // Panchayats) so the next level's list appears — only when this step is active.
  const selectedNode = useMemo(() => findSelectedNode(destTree), [destTree]);
  useEffect(() => {
    if (active && selectedNode && selectedNode.kind !== 'booth') loadChildren(selectedNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id, active]);

  // The org's internal hierarchy tree (left panel) — populated by the Excel upload.
  // Fetched only when this step is active (avoids calling it from step 1).
  const { data: nodeTree, isFetching: treeLoading } = useGetNodeTreeQuery(
    { organization: organizationId! },
    { skip: !organizationId || !active },
  );

  // Booths already present in the Internal Organization tree, keyed by area_code — an
  // EC booth sharing a code is shown as already linked.
  const linkedAreas = useMemo(() => collectInternalAreas(nodeTree ?? []), [nodeTree]);

  // Locate a booth (clicked in the Internal Organization panel) in the EC structure via
  // /territory/booth-info/?ps_code=… — shows where that booth sits.
  const [located, setLocated] = useState<{
    code: string;
    name: string;
    loading: boolean;
    info?: BoothInfoDto;
  } | null>(null);
  const locateBooth = useCallback(
    async (areaCode: string, boothName: string) => {
      setLocated({ code: areaCode, name: boothName, loading: true });
      try {
        const res = await dispatch(
          territoryApi.endpoints.getBoothInfo.initiate({ psCode: areaCode }, { subscribe: false }),
        ).unwrap();
        setLocated({ code: areaCode, name: boothName, loading: false, info: res });
      } catch {
        setLocated({ code: areaCode, name: boothName, loading: false });
      }
    },
    [dispatch],
  );

  // Right-panel tree: the located booth's EC structure when one is clicked, else the
  // step-2-derived tree.
  const ecTree = useMemo(
    () => (located?.info ? boothInfoToTree(located.info) : destTree),
    [located, destTree],
  );

  // Link (POST) / de-link (DELETE) / re-link (PATCH) a booth's node-area record. The org
  // tree refetches on success (OrgNode invalidation), keeping the linked state in sync.
  const [createNodeArea] = useCreateNodeAreaMutation();
  const [deleteNodeArea] = useDeleteNodeAreaMutation();
  const [updateNodeArea] = useUpdateNodeAreaMutation();
  // Booth whose link is currently changing — keyed by node-area id (linked) or area_code (linking).
  const [busyAreaId, setBusyAreaId] = useState<number | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const handleUnlink = async (nodeAreaId: number) => {
    setBusyAreaId(nodeAreaId);
    try {
      await deleteNodeArea(nodeAreaId).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not unlink the booth. Try again.') });
    } finally {
      setBusyAreaId(null);
    }
  };

  const handleRelink = async (nodeAreaId: number, organisationNodeId: number) => {
    setBusyAreaId(nodeAreaId);
    try {
      await updateNodeArea({ id: nodeAreaId, organisation_node: organisationNodeId }).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not relink the booth. Try again.') });
    } finally {
      setBusyAreaId(null);
    }
  };

  const handleLink = async (areaCode: string, boothName: string, organisationNodeId: number) => {
    setBusyCode(areaCode);
    try {
      await createNodeArea({
        organisation_node: organisationNodeId,
        area_code: areaCode,
        booth_name: boothName,
      }).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not link the booth. Try again.') });
    } finally {
      setBusyCode(null);
    }
  };

  const [triggerTemplate] = useLazyGetHierarchyTemplateQuery();
  const [uploadHierarchy, { isLoading: uploading }] = useUploadHierarchyMutation();
  const [downloading, setDownloading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prefer the backend's `detail` message when present.
  const errText = (err: unknown, fallback: string) => {
    const data = (err as { data?: unknown })?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const d = (data as { detail?: unknown }).detail;
      if (typeof d === 'string') return d;
    }
    return fallback;
  };

  // Build a client-side .xlsx from the step-3 levels (fallback when there's no org id yet).
  const downloadLocalTemplate = () => {
    const levels = hierarchyLevels && hierarchyLevels.length > 0 ? hierarchyLevels : FALLBACK_LEVELS;
    const emptyRows = Array.from({ length: 20 }, () => levels.map(() => ''));
    const ws = XLSX.utils.aoa_to_sheet([levels, ...emptyRows]);
    ws['!cols'] = levels.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hierarchy');
    XLSX.writeFile(wb, 'organization-hierarchy-template.xlsx');
  };

  // Download the per-org template from the API; fall back to a local one if no org id.
  const downloadTemplate = async () => {
    setFeedback(null);
    if (!organizationId) {
      downloadLocalTemplate();
      return;
    }
    setDownloading(true);
    try {
      const blob = await triggerTemplate({ organization: organizationId }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hierarchy.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: errText(err, 'Could not download the template. Please try again.'),
      });
    } finally {
      setDownloading(false);
    }
  };

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setFeedback(null);
    if (!organizationId) {
      setFeedback({ type: 'error', text: 'Please complete step 1 (create the organization) first.' });
      return;
    }
    try {
      await uploadHierarchy({ organization: organizationId, file }).unwrap();
      setFeedback({ type: 'success', text: `“${file.name}” uploaded successfully.` });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: errText(err, `Could not upload “${file.name}”. Check the file and try again.`),
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Info cards */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <InfoCard
          Icon={TemplateDocIcon}
          iconClass="text-brand"
          badgeClass="bg-[#cdd4ff]"
          title="Hierarchy Template"
          subtitle="Download the standardized structure"
          action={
            <button
              type="button"
              onClick={() => void downloadTemplate()}
              disabled={downloading}
              className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
            >
              {downloading ? 'Downloading…' : 'Download XLSX'}
            </button>
          }
        />
        <InfoCard
          Icon={BulkUploadIcon}
          iconClass="text-[#943700]"
          badgeClass="bg-[#ffdbcd]"
          title="Bulk Upload Hierarchy"
          subtitle="Import multiple organization levels"
          action={
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={onFilePicked}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
              >
                {uploading ? 'Uploading…' : 'Select Files'}
              </button>
            </>
          }
        />
      </div>

      {feedback && (
        <p
          className={[
            'rounded-lg border px-4 py-3 text-sm font-medium',
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-[#ba1a1a]',
          ].join(' ')}
        >
          {feedback.text}
        </p>
      )}

      {/* Explorer */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {/* SOURCE */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:w-[400px]">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Source</span>
              <h2 className="text-base font-semibold text-ink">Internal Organization</h2>
            </div>
            <button type="button" aria-label="Filter" className="text-ink">
              <FilterIcon className="h-[10px] w-[15px]" />
            </button>
          </div>

          <div className="border-b border-line px-4 py-4">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[13.5px] -translate-y-1/2 text-ink-secondary/70" />
              <input
                type="search"
                placeholder="Search units..."
                className="w-full rounded-lg bg-[#e5eeff] py-2.5 pl-10 pr-4 text-[13px] text-ink placeholder:text-ink-secondary/70 focus:outline-none focus:ring-2 focus:ring-brand/15"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4">
            {treeLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            ) : nodeTree && nodeTree.length > 0 ? (
              <InternalOrgTree
                nodes={nodeTree}
                onBoothClick={locateBooth}
                activeCode={located?.code}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <BulkUploadIcon className="size-6 text-ink-secondary/50" />
                <p className="text-sm text-ink-secondary">
                  Upload the hierarchy Excel to populate the internal organization structure.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* DESTINATION */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:flex-1">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Destination</span>
              <h2 className="text-base font-semibold text-ink">Election Commission Structure</h2>
              {loadingNodes.size > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                  <Spinner size={14} /> Loading…
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-ink">
              <button
                type="button"
                aria-label="Columns view"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <ColumnsIcon className="h-[11px] w-[15px]" />
              </button>
              <button
                type="button"
                aria-label="More options"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <KebabIcon className="h-[13px] w-[3px]" />
              </button>
            </div>
          </div>

          {/* Located booth (clicked in the Internal Organization panel) */}
          {located && (
            <div className="flex items-start justify-between gap-3 border-b border-brand/20 border-l-4 border-l-brand bg-gradient-to-r from-[#dce9ff] to-[#eff4ff] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
                  <HierarchyNodesIcon className="size-3.5" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-brand-dark">
                    Booth location · {located.code}
                  </span>
                  {located.loading ? (
                    <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                      <Spinner size={12} /> Locating “{located.name}”…
                    </span>
                  ) : located.info ? (
                    <span className="text-sm font-semibold text-brand-dark">
                      {boothLocationPath(located.info) || located.name}
                    </span>
                  ) : (
                    <span className="text-xs text-ink-secondary">
                      No location found for this booth.
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                aria-label="Clear"
                onClick={() => setLocated(null)}
                className="shrink-0 rounded px-1 text-lg leading-none text-ink-secondary transition-colors hover:text-ink"
              >
                ×
              </button>
            </div>
          )}

          {/* Miller-columns view (breadcrumb + LEVEL N columns) */}
          {ecTree.length > 0 ? (
            <DestinationColumns
              tree={ecTree}
              getChildren={getChildren}
              onDrill={loadChildren}
              loadingNodes={loadingNodes}
              linkedAreas={linkedAreas}
              sourceTree={nodeTree ?? []}
              onLink={handleLink}
              onUnlink={handleUnlink}
              onRelink={handleRelink}
              busyAreaId={busyAreaId}
              busyCode={busyCode}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 bg-[#f8f9ff] px-6 text-center">
              <HierarchyNodesIcon className="size-6 text-ink-secondary/50" />
              <p className="text-sm text-ink-secondary">
                Select constituency boundaries in Step 2 to populate the Election Commission
                structure.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end border-t border-line bg-white px-4 py-4">
            <button
              type="button"
              onClick={onConfirm}
              className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-xs font-semibold tracking-[0.24px] text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Confirm Selection
              <ArrowRightIcon className="h-[9px] w-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
